import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Dialog, toast } from '@utima/ui';
import { memo, useEffect, useMemo, useState } from 'react';

import type { KBContentRow } from './kbTypes';
import {
  PRIORITY_KEEP_UNCHANGED_VALUE,
  PrioritySelect,
} from './PrioritySelect';
import type { useKBMutations } from '../hooks/useKBMutations';
import { PRIORITY_WEIGHT_VALUES, weightToPriorityScale } from '../lib/priority';

type BulkWeightModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedRows: KBContentRow[];
  mutations: ReturnType<typeof useKBMutations>;
};

function getUniformWeight(weights: number[]): number | undefined {
  if (weights.length === 0) {
    return undefined;
  }

  const first = weights[0];

  return weights.every(weight => weight === first) ? first : undefined;
}

export const BulkWeightModal = memo(function BulkWeightModal({
  open,
  onOpenChange,
  selectedRows,
  mutations,
}: BulkWeightModalProps) {
  const { t } = useLingui();
  const [selectedWeight, setSelectedWeight] = useState<string>(
    PRIORITY_KEEP_UNCHANGED_VALUE,
  );
  const [isSaving, setIsSaving] = useState(false);

  const documentRows = useMemo(
    () => selectedRows.filter(row => row.rowType === 'document'),
    [selectedRows],
  );
  const folderRows = useMemo(
    () => selectedRows.filter(row => row.rowType === 'folder'),
    [selectedRows],
  );

  const documentIds = useMemo(
    () => documentRows.map(row => row.id),
    [documentRows],
  );
  const folderIds = useMemo(() => folderRows.map(row => row.id), [folderRows]);

  useEffect(() => {
    if (!open) {
      setSelectedWeight(PRIORITY_KEEP_UNCHANGED_VALUE);

      return;
    }

    const weights = [
      ...documentRows
        .map(row => row.data.weight)
        .filter((weight): weight is number => typeof weight === 'number'),
      ...folderRows
        .map(row => row.data.defaultWeight)
        .filter((weight): weight is number => typeof weight === 'number'),
    ];
    const uniformWeight = getUniformWeight(weights);

    if (uniformWeight === undefined) {
      setSelectedWeight(PRIORITY_KEEP_UNCHANGED_VALUE);
    } else {
      setSelectedWeight(
        PRIORITY_WEIGHT_VALUES[weightToPriorityScale(uniformWeight)],
      );
    }
  }, [documentRows, folderRows, open]);

  const handleSave = async () => {
    if (documentIds.length === 0 && folderIds.length === 0) {
      toast.error(t`No documents or folders selected.`);

      return;
    }

    if (selectedWeight === PRIORITY_KEEP_UNCHANGED_VALUE) {
      toast.error(t`Choose priority to apply.`);

      return;
    }

    const weight = Number.parseFloat(selectedWeight);

    setIsSaving(true);

    try {
      const updates: Promise<unknown>[] = [];

      if (documentIds.length > 0) {
        updates.push(
          mutations.bulkWeight.mutateAsync({
            documentIds,
            weight,
          }),
        );
      }

      if (folderIds.length > 0) {
        updates.push(
          ...folderIds.map(folderId =>
            mutations.updateFolder.mutateAsync({
              id: folderId,
              defaultWeight: weight,
            }),
          ),
        );
      }

      await Promise.all(updates);
      toast.success(t`Priority updated`);
      onOpenChange(false);
    } catch {
      toast.error(t`Failed to update priority`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className='w-full max-w-[560px]'>
        <Dialog.Header>
          <Dialog.Title>
            <Trans>Priority settings</Trans>
          </Dialog.Title>
          <Dialog.Description>
            <Trans>
              Set one priority for selected documents and default priority for
              selected folders.
            </Trans>
          </Dialog.Description>
        </Dialog.Header>

        <div className='space-y-4 py-2'>
          {documentIds.length > 0 && (
            <div className='rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-fg'>
              <Trans>
                Selected documents: {documentIds.length}. Priority will be
                updated on each document.
              </Trans>
            </div>
          )}

          {folderIds.length > 0 && (
            <div className='rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-fg'>
              <Trans>
                Selected folders: {folderIds.length}. Default priority for new
                documents in those folders will be updated. Existing documents
                are not changed.
              </Trans>
            </div>
          )}

          <div className='space-y-1.5'>
            <p className='text-sm font-medium'>
              <Trans>Priority</Trans>
            </p>
            <PrioritySelect
              value={selectedWeight}
              onValueChange={setSelectedWeight}
              includeKeepUnchanged
            />
          </div>
        </div>

        <Dialog.Footer>
          <Button variant='ghost' outline onClick={() => onOpenChange(false)}>
            <Trans>Cancel</Trans>
          </Button>
          <Button onClick={handleSave} loading={isSaving}>
            <Trans>Save</Trans>
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
});
